import { yupResolver } from '@hookform/resolvers/yup';
import Link from '@mui/icons-material/Link';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AuthMethod, ModelError_Item, ResourceType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { capitalize } from 'lodash-es';
import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { ControllerProps, FieldErrors, useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import { ReadOnlyField } from '~components/FormFields/ReadOnlyField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { SystemRoles } from '~constants/enums';
import { FormStateChangeProps } from '~formsShared/types';
import { AdminUser } from '~hooks/admin/useUsersAdmin/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { Driver } from '~hooks/useDrivers/models';
import { ResourceScope } from '~hooks/useResourceScopes/models';
import { useResourceScopes } from '~hooks/useResourceScopes/useResourceScopes';
import { Site } from '~hooks/useSites/models';
import { useSites } from '~hooks/useSites/useSites';
import { RoleItem, User } from '~hooks/useUsers/models';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { allowedTimezones } from '~utils/dateTime';

import { LoadingSpinner } from '../Order/ordersDispatchStyledComponents';
import {
  driverFormValidationSchema,
  userEmailSchema,
  userPhoneSchema,
  userWithEmailAndOmitedPhoneValidationSchema,
  userWithEmailOrPhoneValidationSchema,
  userWithRequiredEmailAndOmitedPhoneValidationSchema,
  userWithRequiredEmailValidationSchema,
} from './userFormValidationSchema';

export interface UserFormHandler {
  submit: (callback: (data: UserValidationDTOWithSitesDiff) => void) => void;
  resetForm: (callback?: () => void) => void;
}

type UserValidationSchema =
  | typeof userWithRequiredEmailValidationSchema
  | typeof userWithRequiredEmailAndOmitedPhoneValidationSchema
  | typeof userWithEmailOrPhoneValidationSchema
  | typeof userWithEmailAndOmitedPhoneValidationSchema;

type UserValidationDTO =
  | yup.InferType<typeof userWithRequiredEmailValidationSchema>
  | yup.InferType<typeof userWithRequiredEmailAndOmitedPhoneValidationSchema>
  | yup.InferType<typeof userWithEmailOrPhoneValidationSchema>
  | yup.InferType<typeof userWithEmailAndOmitedPhoneValidationSchema>;

export type SitesDiff = { resourceIdsToDelete: string[]; siteIdsToAdd: string[] };

export type UserValidationDTOWithSitesDiff = UserValidationDTO & { sitesDiff: SitesDiff };

export interface UserFormStateChangeProps extends FormStateChangeProps {
  hasValidEmail: boolean;
  hasValidPhone: boolean;
  hasDriverRole?: boolean;
}

// Where this form is being rendered from
enum UserFormSource {
  ADMIN_USERS = 'admin_users',
  USER_PROFILE = 'user_profile',
}

interface UserProfileFormProviderProps {
  availableAuthMethods: AuthMethod[];
  defaultUser: Nullable<User | AdminUser>;
  defaultDriver?: Driver;
  editable: boolean;
  disableUserRolesField?: boolean;
  onFormDirty?: (isDirty: boolean) => void;
  onFormStateChange: ({
    isValid,
    isDirty,
    hasValidEmail,
    hasValidPhone,
  }: UserFormStateChangeProps) => void;
  userRolesOptions: Array<RoleItem>;
  allowSharing?: boolean;
  companyShares?: Array<CompanyShare>;
  source?: `${UserFormSource}`;
  errors?: ModelError_Item[];
  isManagedUser?: boolean;
}

interface UserProfileFormProps extends Omit<
  UserProfileFormProviderProps,
  'availableAuthMethods'
> {
  validationSchema: UserValidationSchema;
  sites: ResourceScope[];
}

// Once entered, phone number cannot be edited, but we have registered users with invalid numbers.
// This is blocking admins from editing user's fields different from phone number, so for those
// Cases we skip the validation.
const shouldSkipPhoneNumberValidation = (phoneNumber: Nullable<string>) => {
  return Boolean(phoneNumber);
};

const validateEmail = (email: Nullable<string>) => {
  let isValid = true;

  try {
    const input = email || '';

    if (input.length) {
      userEmailSchema.validateSync(input);
    } else {
      isValid = false;
    }
  } catch {
    isValid = false;
  }

  return isValid;
};

const validatePhone = (phone: Nullable<string>) => {
  let isValid = true;

  try {
    const input = phone || '';

    if (input.length) {
      userPhoneSchema.validateSync(input);
    } else {
      isValid = false;
    }
  } catch {
    isValid = false;
  }

  return isValid;
};

type ResourceScopesState = {
  hasFailed: boolean;
  isLoading: boolean;
  sites: ResourceScope[];
};

const UserFormProvider = forwardRef(function UserFormProvider(
  {
    availableAuthMethods,
    defaultUser,
    editable,
    source,
    ...rest
  }: UserProfileFormProviderProps,
  ref: Ref<UserFormHandler>,
) {
  const isEditing = Boolean(defaultUser?.id);

  const { getAllResourceScopes } = useResourceScopes();
  const [resourceScopes, setResourceScopes] = useState<ResourceScopesState>({
    hasFailed: false,
    isLoading: isEditing,
    sites: [],
  });

  useEffect(() => {
    if (isEditing) {
      setResourceScopes((state) => ({ ...state, isLoading: true }));

      const loadResourceScopes = async () => {
        try {
          const response = await getAllResourceScopes({
            userId: String(defaultUser?.id),
            resourceType: ResourceType.SITE,
            companyId: defaultUser?.company?.id,
          });

          setResourceScopes({
            hasFailed: false,
            isLoading: false,
            sites: response.reduce((acc, resourceScope) => {
              if (resourceScope.resource.type === ResourceType.SITE) {
                acc.push(resourceScope);
              }

              return acc;
            }, [] as ResourceScope[]),
          });
        } catch {
          setResourceScopes({ hasFailed: true, isLoading: false, sites: [] });
        }
      };

      void loadResourceScopes();
    }
  }, [isEditing]);

  const validationSchema = useMemo(() => {
    const skipPhoneNumberValidation = shouldSkipPhoneNumberValidation(defaultUser?.phone);

    if (availableAuthMethods.includes(AuthMethod.OTP_SMS)) {
      return skipPhoneNumberValidation
        ? userWithEmailAndOmitedPhoneValidationSchema
        : userWithEmailOrPhoneValidationSchema;
    }

    return skipPhoneNumberValidation
      ? userWithRequiredEmailAndOmitedPhoneValidationSchema
      : userWithRequiredEmailValidationSchema;
  }, [availableAuthMethods, defaultUser?.phone]);

  if (resourceScopes.isLoading) {
    return <LoadingSpinner isVisible />;
  }

  // An empty site list reads as "all sites visible", so a failed fetch must not render the form.
  if (resourceScopes.hasFailed) {
    return null;
  }

  return (
    <UserForm
      ref={ref}
      editable={editable}
      validationSchema={validationSchema}
      defaultUser={defaultUser}
      source={source}
      sites={resourceScopes.sites}
      {...rest}
    />
  );
});

const UserForm = forwardRef(function UserForm(
  {
    allowSharing = false,
    companyShares,
    defaultDriver,
    defaultUser,
    disableUserRolesField,
    editable,
    errors: errorsProp,
    onFormStateChange,
    sites,
    source,
    userRolesOptions = [],
    validationSchema,
    isManagedUser = false,
  }: UserProfileFormProps,
  ref: Ref<UserFormHandler>,
) {
  const { userStore } = useStores();
  const { getAllConnectedCompaniesByCompanyIdTypeahead } = useCompany();
  const { getCompanySitesTypeahead } = useSites();
  const theme = useTheme();

  const isSharedDriver = Boolean(defaultDriver?.companyShare?.id);

  const isManagedDriver = defaultDriver?.managed;
  const defaultSites = sites.map((site) => ({
    id: site.resource.id,
    name: site.resource.name,
    resourceId: String(site.id),
  }));

  const isAdminUsersSource = source === UserFormSource.ADMIN_USERS;
  // Kept out of the admin surface's generic Role field/seed so the toggle below is the
  // only control for it; sourced from defaultUser (not the async userRolesOptions fetch)
  // so a slow/failed roles load can't wipe out a held role on an unrelated save.
  const existingBillingPortalRole = isAdminUsersSource
    ? (defaultUser?.userRoles || []).find(
        (role) => role.name === SystemRoles.BILLING_PORTAL,
      )
    : undefined;
  const userRolesWithoutBillingPortalAccess = (defaultUser?.userRoles || []).filter(
    (role) => role.name !== SystemRoles.BILLING_PORTAL,
  );
  const assignableUserRolesOptions = isAdminUsersSource
    ? userRolesOptions.filter((role) => role.name !== SystemRoles.BILLING_PORTAL)
    : userRolesOptions;

  const defaultUserValues = {
    firstName: defaultUser?.firstName || '',
    lastName: defaultUser?.lastName || '',
    userRoles: isAdminUsersSource
      ? [...userRolesWithoutBillingPortalAccess]
      : [...(defaultUser?.userRoles || [])],
    phone: defaultUser?.phone,
    email: defaultUser?.email,
    company: defaultUser?.company || userStore.currentCompanies[0] || null,
    timeZone: defaultUser?.timeZone || userStore.userCompany?.timeZone || '',
    textToAcceptFieldEnabled: isAdminUsersSource,
    textToAcceptFieldValue: defaultUser?.textToAcceptEnabled,
    billingPortalAccessFieldEnabled: isAdminUsersSource,
    billingPortalAccessFieldValue: (defaultUser?.userRoles || []).some(
      (role) => role.name === SystemRoles.BILLING_PORTAL,
    ),
    hideRatesFieldEnabled:
      source === UserFormSource.ADMIN_USERS &&
      userStore.getPermissions().canEditUserHideRate,
    hideRatesFieldValue: defaultUser?.hideRates,
    noLocationAlertFieldEnabled: source === UserFormSource.ADMIN_USERS && !!defaultUser,
    noLocationAlertFieldValue: defaultUser?.preferences?.notifications?.noLocationAlert
      ?.length
      ? true
      : false,
    externalId: defaultUser?.externalId,
    accountingId: defaultUser?.accountingId,
    equipment: defaultUser?.equipment,
  };

  const defaultDriverValues = {
    firstName: defaultDriver?.firstName || '',
    lastName: defaultDriver?.lastName || '',
    phone: defaultDriver?.phone,
    email: defaultDriver?.email,
    company: defaultDriver?.company || null,
  };

  const defaultValues = defaultDriver ? defaultDriverValues : defaultUserValues;
  const {
    control,
    formState: { errors, isValid, isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(defaultDriver ? driverFormValidationSchema : validationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      ...defaultValues,
      companyShares: companyShares?.length
        ? companyShares.map((companyShare) => ({
            id: companyShare.id,
            receiverCompany: companyShare.receiverCompany,
            rateOverride: companyShare.rateOverride ?? false,
          }))
        : [],
      sites: defaultSites,
    },
  });

  const watchedCompany = watch('company');
  const watchedUserRoles = watch('userRoles');
  const watchedEmail = watch('email');
  const watchedPhone = watch('phone');
  const watchedSites = watch('sites') as unknown as Site[] | null;

  const {
    fields: companySharesFields,
    append: appendCompanyShares,
    remove: removeCompanyShares,
  } = useFieldArray({
    control,
    name: 'companyShares',
  });

  const sitesDiff: SitesDiff = useMemo(() => {
    const resourceIdsToDelete = defaultSites.reduce((acc, defaultSite) => {
      const site = watchedSites?.find((site) => site.id === defaultSite.id);

      if (!site) {
        acc.push(defaultSite.resourceId);
      }

      return acc;
    }, [] as string[]);

    const siteIdsToAdd = (watchedSites ?? []).reduce((acc, watchedSite) => {
      const site = defaultSites.find((site) => site.id === watchedSite.id);

      if (!site) {
        acc.push(watchedSite.id);
      }

      return acc;
    }, [] as string[]);

    return { resourceIdsToDelete, siteIdsToAdd };
  }, [defaultSites, watchedSites]);

  // Update default values once async companyShares are loaded
  useEffect(() => {
    if (companyShares?.length) {
      const updatedDefaultValues = {
        ...defaultValues,
        companyShares: companyShares.map((companyShare) => ({
          id: companyShare.id,
          receiverCompany: companyShare.receiverCompany,
          rateOverride: companyShare.rateOverride ?? false,
        })),
      };
      reset(updatedDefaultValues);
    }
  }, [companyShares, reset, JSON.stringify(defaultValues)]);

  const hasValidEmail = useMemo(() => {
    return validateEmail(watchedEmail);
  }, [watchedEmail]);

  const hasValidPhone = useMemo(() => {
    return validatePhone(watchedPhone);
  }, [watchedPhone]);

  const hasDriverRole = useMemo(() => {
    return (
      watchedUserRoles &&
      watchedUserRoles.some((role) => role.name.toLowerCase() === 'driver')
    );
  }, [watchedUserRoles]);

  const hasViewerRole = useMemo(() => {
    return (
      watchedUserRoles &&
      watchedUserRoles.some((role) => role.name.toLowerCase() === 'viewer')
    );
  }, [watchedUserRoles]);

  const { isEmailFieldOptional, isPhoneFieldOptional } = useMemo(() => {
    const emailDesc = validationSchema.describe().fields.email as yup.SchemaDescription;
    const phoneDesc = validationSchema.describe().fields.phone as yup.SchemaDescription;

    return {
      isEmailFieldOptional: emailDesc ? emailDesc.nullable || emailDesc.optional : true,
      isPhoneFieldOptional: phoneDesc ? phoneDesc.nullable || phoneDesc.optional : true,
    };
  }, [validationSchema]);

  useEffect(() => {
    if (!hasViewerRole) {
      setValue('sites', []);
    }
  }, [hasViewerRole]);

  useImperativeHandle(ref, () => ({
    submit(callback) {
      handleSubmit((data) => {
        if (!isAdminUsersSource || !('userRoles' in data)) {
          callback({ ...data, sitesDiff } as UserValidationDTOWithSitesDiff);

          return;
        }

        const submittedUserRoles = data.userRoles as RoleItem[];
        const viewerGrantedBillingPortalAccess = Boolean(
          data.billingPortalAccessFieldValue,
        );
        const billingPortalRole =
          existingBillingPortalRole ??
          userRolesOptions.find((role) => role.name === SystemRoles.BILLING_PORTAL);
        const userRolesWithBillingPortalAccess =
          viewerGrantedBillingPortalAccess && billingPortalRole
            ? [...submittedUserRoles, billingPortalRole]
            : submittedUserRoles.filter(
                (role) => role.name !== SystemRoles.BILLING_PORTAL,
              );

        callback({
          ...data,
          userRoles: userRolesWithBillingPortalAccess,
          sitesDiff,
        } as UserValidationDTOWithSitesDiff);
      })();
    },
    resetForm(callback) {
      reset();
      callback?.();
    },
  }));

  useEffect(() => {
    onFormStateChange({ isValid, isDirty, hasValidEmail, hasValidPhone });
  }, [isValid, isDirty, hasValidEmail, hasValidPhone]);

  useEffect(() => {
    if (errorsProp) {
      for (const error of errorsProp) {
        const newErrorField = getErrorMapping(error.field, error.message);
        if (newErrorField.field && newErrorField.message) {
          setError(newErrorField.field as keyof ReturnType<typeof setError>, {
            type: 'manual',
            message: capitalize(newErrorField.message as string),
          });
        }
      }
    }
  }, [errorsProp]);

  return (
    <form>
      <Grid container spacing={2}>
        {!isManagedUser && source !== UserFormSource.USER_PROFILE && (
          <Grid size={{ xs: 12 }}>
            <CompanySelectorFormField
              sx={{ width: '100%', minWidth: '100%' }}
              name={'company'}
              isRequired={true}
              disabled={!!defaultUser?.company?.id || !!defaultDriver?.company?.id}
              errors={errors}
              control={control}
            />
          </Grid>
        )}
        <Grid size={{ xs: 6 }}>
          {editable ? (
            <TextFormField
              control={control}
              errors={errors}
              name="firstName"
              label={t('form_fields.first_name')}
              isRequired={true}
              disabled={isSharedDriver && !isManagedDriver}
            />
          ) : (
            <ReadOnlyField
              label={t('form_fields.first_name')}
              value={watch('firstName')}
            />
          )}
        </Grid>
        <Grid size={{ xs: 6 }}>
          {editable ? (
            <TextFormField
              control={control}
              errors={errors}
              name="lastName"
              label={t('form_fields.last_name')}
              isRequired={true}
              disabled={isSharedDriver && !isManagedDriver}
            />
          ) : (
            <ReadOnlyField label={t('form_fields.last_name')} value={watch('lastName')} />
          )}
        </Grid>
        <Grid size={{ xs: 6 }}>
          {editable ? (
            <TextFormField
              control={control}
              errors={errors}
              name="email"
              type="email"
              label={t('form_fields.email')}
              isRequired={!isEmailFieldOptional}
              disabled={isSharedDriver && !isManagedDriver}
            />
          ) : (
            <ReadOnlyField label={t('form_fields.email')} value={watch('email')} />
          )}
        </Grid>
        <Grid size={{ xs: 6 }}>
          {editable ? (
            <PhoneCodeFlagInput
              control={control as unknown as ControllerProps['control']}
              errors={errors as unknown as FieldErrors}
              name="phone"
              label={t('form_fields.phone')}
              disabled={isSharedDriver && !isManagedDriver}
              isRequired={!isPhoneFieldOptional}
            />
          ) : (
            <ReadOnlyField label={t('form_fields.phone')} value={watch('phone')} />
          )}
        </Grid>
        {defaultDriver && (
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                name="externalId"
                type="text"
                label={t('form_fields.external_id')}
                disabled={isSharedDriver}
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.external_id')}
                value={watch('externalId')}
              />
            )}
          </Grid>
        )}

        {!defaultDriver && (
          <>
            <Grid size={{ xs: 6 }}>
              {editable ? (
                <AutocompleteFormField
                  control={control}
                  name="userRoles"
                  errors={errors}
                  list={assignableUserRolesOptions}
                  label={t('form_fields.role')}
                  isRequired={true}
                  clearable={false}
                  multiple={true}
                  disabled={disableUserRolesField || isSharedDriver}
                  getValue={(item) => item?.id}
                  getLabel={(item) => item.name}
                  limitTags={2}
                />
              ) : (
                <ReadOnlyField
                  label={t('form_fields.role')}
                  value={watchedUserRoles?.map((role) => role.name).join(', ')}
                />
              )}
            </Grid>
            <Grid size={{ xs: 6 }}>
              {editable ? (
                <AutocompleteFormField
                  control={control}
                  name="timeZone"
                  errors={errors}
                  list={allowedTimezones}
                  label={t('form_fields.time_zone')}
                  isRequired={true}
                  clearable={false}
                  getValue={(item) => item}
                  getLabel={(item) => item}
                  disabled={isSharedDriver}
                />
              ) : (
                <ReadOnlyField
                  label={t('form_fields.time_zone')}
                  value={watch('timeZone')}
                />
              )}
            </Grid>
            <Grid size={{ xs: 6 }}>
              {editable ? (
                <TextFormField
                  control={control}
                  errors={errors}
                  name="externalId"
                  type="text"
                  label={t('form_fields.external_id')}
                  disabled={isSharedDriver}
                />
              ) : (
                <ReadOnlyField
                  label={t('form_fields.external_id')}
                  value={watch('externalId')}
                />
              )}
            </Grid>
            <Grid size={{ xs: 6 }}>
              {editable ? (
                <TextFormField
                  control={control}
                  errors={errors}
                  name="accountingId"
                  type="text"
                  label={t('form_fields.accounting_id')}
                  disabled={isSharedDriver}
                />
              ) : (
                <ReadOnlyField
                  label={t('form_fields.accounting_id')}
                  value={watch('accountingId')}
                />
              )}
            </Grid>

            {getValues('textToAcceptFieldEnabled') && editable && (
              <Grid size={{ xs: 12 }}>
                <CheckBoxFormField
                  control={control}
                  disabled={isSharedDriver}
                  label={t('form_fields.text_to_accept_enabled')}
                  name="textToAcceptFieldValue"
                  value={Boolean(getValues('textToAcceptFieldValue'))}
                />
              </Grid>
            )}

            {getValues('noLocationAlertFieldEnabled') && editable && (
              <Grid size={{ xs: 12 }}>
                <CheckBoxFormField
                  control={control}
                  disabled={isSharedDriver}
                  label={t('form_fields.no_location_alert_enabled')}
                  name="noLocationAlertFieldValue"
                  value={Boolean(getValues('noLocationAlertFieldValue'))}
                />
              </Grid>
            )}

            {getValues('billingPortalAccessFieldEnabled') && editable && (
              <Grid size={{ xs: 12 }}>
                <CheckBoxFormField
                  control={control}
                  disabled={isSharedDriver}
                  label={t('form_fields.billing_portal_access_enabled')}
                  name="billingPortalAccessFieldValue"
                  value={Boolean(getValues('billingPortalAccessFieldValue'))}
                />
                <Typography variant="caption" color="secondary" sx={{ display: 'block' }}>
                  {t('form_fields.billing_portal_access_enabled_helper')}
                </Typography>
              </Grid>
            )}

            {getValues('hideRatesFieldEnabled') && editable && (
              <Grid size={{ xs: 12 }}>
                <CheckBoxFormField
                  control={control}
                  disabled={isSharedDriver}
                  errors={errors}
                  label={t('form_fields.hide_rates')}
                  name="hideRatesFieldValue"
                  value={Boolean(getValues('hideRatesFieldValue'))}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
      {/* Viewer site selection */}
      {hasViewerRole && (
        <Grid container sx={{ mt: 2 }}>
          <Typography variant="form-section-title" sx={{ mb: 1, fontSize: '14px' }}>
            {t('user.form.site_permissions')}
          </Typography>

          {editable ? (
            <Grid container size={{ xs: 12 }} spacing={2}>
              <Grid size={{ xs: 6 }}>
                <AutocompleteAsyncFormField
                  asyncCallback={getCompanySitesTypeahead}
                  control={control}
                  disabled={isSharedDriver}
                  errors={errors}
                  extraRequestOptions={{
                    companyId: watchedCompany?.id || '',
                    onlyActiveSites: true,
                  }}
                  filterSelectedOptions={false}
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  hideChipOverflow
                  hideLabel
                  limitTags={1}
                  multiple
                  name="sites"
                  renderOption={(props, option, state) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Checkbox checked={state.selected} sx={{ mr: 1, padding: 0 }} />
                        {option.companyShare && (
                          <Link
                            sx={{
                              color: theme.brandV2.colors.treadGreen,
                              fontSize: '20px',
                              mr: 1,
                            }}
                          />
                        )}
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 6 }} sx={{ mt: -1 }}>
                <Typography
                  variant={'caption'}
                  color={'secondary'}
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {t('user.form.site_access_list')}
                </Typography>
                <Typography variant={'body2'}>
                  {watchedSites && watchedSites.length > 0
                    ? watchedSites.map((site) => site.name).join(', ')
                    : t('user.form.all_sites_visible')}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid container size={{ xs: 12 }} spacing={2}>
              <Grid size={{ xs: 12 }}>
                <ReadOnlyField
                  label={t('user.form.site_access_list')}
                  value={
                    watchedSites && watchedSites.length > 0
                      ? watchedSites.map((site) => site.name).join(', ')
                      : t('user.form.all_sites_visible')
                  }
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
      {/* Shared driver configuration */}
      {(hasDriverRole || !!defaultDriver) && allowSharing && (
        <Grid container sx={{ mt: 2 }}>
          <Typography variant="form-section-title" sx={{ mb: 1 }}>
            {t('user.form.shared_driver_configuration')}
          </Typography>
          <Typography variant={'body2'} sx={{ mb: 2 }}>
            {t('user.form.shared_driver_configuration_hint')}
          </Typography>

          {isSharedDriver && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mb: 2,
                width: '100%',
                border: `1px solid ${theme.palette.divider}`,
                padding: '5px',
              }}
            >
              <Typography variant={'body2'}>
                {t('user.form.shared_from_company')}{' '}
                {defaultDriver?.companyShare?.senderCompany?.legalName}
              </Typography>
            </Box>
          )}

          {/* Company Shares */}
          {companySharesFields.map((item, index) => {
            return (
              <Grid container size={{ xs: 12 }} spacing={2} key={item.id}>
                <Grid size={{ xs: 6 }}>
                  <AutocompleteAsyncFormField
                    control={control}
                    errors={errors}
                    name={`companyShares[${index}].receiverCompany`}
                    getValue={(item) => item.id}
                    getLabel={(item) => item.legalName}
                    asyncCallback={getAllConnectedCompaniesByCompanyIdTypeahead}
                    extraRequestOptions={{
                      companyId: isSharedDriver
                        ? userStore.currentCompanies[0].id
                        : watchedCompany?.id || '',
                    }}
                    label={t('user.form.select_share_to_companies')}
                  />
                </Grid>
                <Grid size={{ xs: 5 }}>
                  <CheckBoxFormField
                    control={control}
                    errors={errors}
                    name={`companyShares[${index}].rateOverride`}
                    label={t('form_fields.share_pay_rate')}
                    value={false}
                    sx={{ mt: 2 }}
                  />
                </Grid>

                <Grid size={{ xs: 1 }}>
                  <RemoveItemButton
                    action={() => {
                      removeCompanyShares(index);
                    }}
                    sx={{
                      ml: 1,
                      mt: 3,
                      cursor: 'pointer',
                    }}
                  />
                </Grid>
              </Grid>
            );
          })}

          <PseudoLink
            title={`+ ${t('administration.equipment.add_shared_company')}`}
            sx={{
              mt: 2,
              display: 'inline-flex',
            }}
            action={() => {
              appendCompanyShares({
                id: '',
                receiverCompany: { id: '', legalName: '' } as CompanyBasic,
                rateOverride: false,
              });
            }}
          />
        </Grid>
      )}
    </form>
  );
});

const getErrorMapping = (fieldName: string, defaultMessage: string) => {
  switch (fieldName) {
    case 'base':
      return {
        field: 'firstName',
        message: defaultMessage,
      };
    case 'hide_rates':
      return {
        field: 'hideRatesFieldValue',
        message: defaultMessage,
      };
    default:
      return {
        field: fieldName,
        message: defaultMessage,
      };
  }
};

export { UserFormProvider as UserForm };
